import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import service from '../service';
import type {
  ContentSlot,
  CreateContentSlotPayload,
  SaveContentSlotDraftPayload,
  UpdateContentSlotPayload,
} from '@Types/contentSlot';
import { contentSlotQueryKeys } from './useContentSlots';

/**
 * Content-slot mutations. Every mutation invalidates the slot's detail and
 * the app's list, so the header picker and any other consumers stay fresh.
 */

const retryServerErrorsOnly = (failureCount: number, error: unknown) => {
  const status = (error as { status?: number })?.status ?? null;
  const isServerOrNetwork = status == null || status >= 500;
  return failureCount < 2 && isServerOrNetwork;
};

function useInvalidateSlot() {
  const queryClient = useQueryClient();
  return (slot?: ContentSlot) => {
    if (slot) {
      queryClient.invalidateQueries({
        queryKey: contentSlotQueryKeys.detail(slot.id),
      });
      queryClient.invalidateQueries({
        queryKey: contentSlotQueryKeys.list(slot.appId),
      });
    } else {
      queryClient.invalidateQueries({ queryKey: contentSlotQueryKeys.all });
    }
  };
}

export function useCreateContentSlot(
  options?: UseMutationOptions<ContentSlot, Error, CreateContentSlotPayload>,
) {
  const invalidate = useInvalidateSlot();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey: ['contentSlot', 'create'],
    mutationFn: async (payload: CreateContentSlotPayload) =>
      service.createContentSlot(payload),
    retry: retryServerErrorsOnly,
    ...restOptions,
    onSuccess: (data, variables, context, mutation) => {
      invalidate(data);
      onSuccess?.(data, variables, context, mutation);
    },
  });
}

export function useUpdateContentSlot(
  options?: UseMutationOptions<
    ContentSlot,
    Error,
    { id: string; payload: UpdateContentSlotPayload }
  >,
) {
  const invalidate = useInvalidateSlot();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey: ['contentSlot', 'update'],
    mutationFn: async ({ id, payload }) =>
      service.updateContentSlot(id, payload),
    retry: retryServerErrorsOnly,
    ...restOptions,
    onSuccess: (data, variables, context, mutation) => {
      invalidate(data);
      onSuccess?.(data, variables, context, mutation);
    },
  });
}

export function useSaveContentSlotDraft(
  options?: UseMutationOptions<
    ContentSlot,
    Error & { status?: number },
    { id: string; payload: SaveContentSlotDraftPayload }
  >,
) {
  const invalidate = useInvalidateSlot();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey: ['contentSlot', 'saveDraft'],
    mutationFn: async ({ id, payload }) =>
      service.saveContentSlotDraft(id, payload),
    // 409 (stale save) must surface immediately — never retried.
    retry: retryServerErrorsOnly,
    ...restOptions,
    onSuccess: (data, variables, context, mutation) => {
      invalidate(data);
      onSuccess?.(data, variables, context, mutation);
    },
  });
}

export function usePublishContentSlot(
  options?: UseMutationOptions<ContentSlot, Error, { id: string }>,
) {
  const invalidate = useInvalidateSlot();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey: ['contentSlot', 'publish'],
    mutationFn: async ({ id }) => service.publishContentSlot(id),
    retry: retryServerErrorsOnly,
    ...restOptions,
    onSuccess: (data, variables, context, mutation) => {
      invalidate(data);
      onSuccess?.(data, variables, context, mutation);
    },
  });
}

export function useUnpublishContentSlot(
  options?: UseMutationOptions<ContentSlot, Error, { id: string }>,
) {
  const invalidate = useInvalidateSlot();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey: ['contentSlot', 'unpublish'],
    mutationFn: async ({ id }) => service.unpublishContentSlot(id),
    retry: retryServerErrorsOnly,
    ...restOptions,
    onSuccess: (data, variables, context, mutation) => {
      invalidate(data);
      onSuccess?.(data, variables, context, mutation);
    },
  });
}

export function useDiscardContentSlotDraft(
  options?: UseMutationOptions<ContentSlot, Error, { id: string }>,
) {
  const invalidate = useInvalidateSlot();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey: ['contentSlot', 'discard'],
    mutationFn: async ({ id }) => service.discardContentSlotDraft(id),
    retry: retryServerErrorsOnly,
    ...restOptions,
    onSuccess: (data, variables, context, mutation) => {
      invalidate(data);
      onSuccess?.(data, variables, context, mutation);
    },
  });
}

export function useDeleteContentSlot(
  options?: UseMutationOptions<void, Error, { id: string }>,
) {
  const invalidate = useInvalidateSlot();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey: ['contentSlot', 'delete'],
    mutationFn: async ({ id }) => service.deleteContentSlot(id),
    retry: retryServerErrorsOnly,
    ...restOptions,
    onSuccess: (data, variables, context, mutation) => {
      invalidate();
      onSuccess?.(data, variables, context, mutation);
    },
  });
}
